import { DateTime } from 'luxon'
import { v4 as uuidv4 } from 'uuid'

import { diggingAjax } from '../ajax/DiggingAjax'
import { store } from '../state/store'
import { DiggingLocationDTO } from '../DTOs/Production/DiggingLocationDTO'
import { appendCachedDiggingSummaryRow } from './patchCachedDiggingSummary'
import { computeShiftId } from './computeShiftId'
import { enqueue } from './queueOps'
import { nextTempId } from './tempId'
import type { TripleShift } from '../types/Shift'
import { isOnlineNow } from './connectivity'
import { formatTimeOnly } from '../utilities/TimeOnly'

function currentShiftId(): number {
  const { selectedDate, selectedShift } = store.getState().shift
  return computeShiftId(selectedDate, selectedShift)
}

export const diggingPatternMutations = {
  async insertDiggingLocation(
    shiftDate: DateTime,
    dayOrNight: TripleShift,
    diggerId: number,
    location: DiggingLocationDTO,
    shiftIdOverride?: number,
  ) {
    if (isOnlineNow()) return diggingAjax.insertDiggingLocation(shiftDate, dayOrNight, diggerId, location)
    const tempId = uuidv4()
    const tempDiggingPatternId = await nextTempId()
    await enqueue({
      entity: 'diggingPattern',
      operation: 'create',
      shiftId: shiftIdOverride ?? currentShiftId(),
      tempId,
      entityId: tempDiggingPatternId,
      data: {
        diggerId,
        patternBlastId: location.patternBlastId,
        startTime: formatTimeOnly(location.startTime),
        blastMasterId: location.blastMasterId,
        pitStageId: location.pitStageId,
      },
    })
    const created = new DiggingLocationDTO({ ...location, diggingPatternId: tempDiggingPatternId })
    appendCachedDiggingSummaryRow(created, shiftDate, dayOrNight, diggerId)

    return { success: true, data: created as DiggingLocationDTO | null }
  },

  async updateDiggingLocation(location: DiggingLocationDTO) {
    if (isOnlineNow()) return diggingAjax.updateDiggingLocation(location)
    await enqueue({
      entity: 'diggingPattern',
      operation: 'update',
      shiftId: currentShiftId(),
      entityId: location.diggingPatternId,
      rowVersion: location.rowVersion,
      data: {
        patternBlastId: location.patternBlastId,
        startTime: formatTimeOnly(location.startTime),
        blastMasterId: location.blastMasterId,
        pitStageId: location.pitStageId,
      },
    })
    return { success: true, data: location as DiggingLocationDTO | null }
  },

  async deleteDiggingPattern(diggingPatternId: number, rowVersion?: string) {
    if (isOnlineNow()) return diggingAjax.deleteDiggingPattern(diggingPatternId)
    await enqueue({
      entity: 'diggingPattern',
      operation: 'delete',
      shiftId: currentShiftId(),
      entityId: diggingPatternId,
      rowVersion,
    })
    return { success: true, data: null as null }
  },
}
